<script setup lang="ts" generic="T">
import debounce from "debounce";
import type { VNode } from "vue";
import {
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useTemplateRef,
  watch,
} from "vue";

export type NonEmptyArray<T> = [T, ...T[]];

export type Column = number[];

export type KeyMapper<T> = (
  item: T,
  column: number,
  row: number,
  index: number,
) => string | number | symbol | undefined;

const {
  columnWidth = 400,
  gapX = 0,
  gapY = 0,
  items,
  maxColumns,
  minColumns = 1,
  rtl = false,
  scrollContainer = null,
  ssrColumns = 0,
} = defineProps<{
  columnWidth?: number | NonEmptyArray<number> | undefined;
  items: T[];
  gapX?: number | undefined;
  gapY?: number | undefined;
  rtl?: boolean | undefined;
  ssrColumns?: number | undefined;
  scrollContainer?: HTMLElement | null | undefined;
  minColumns?: number | undefined;
  maxColumns?: number | undefined;
  keyMapper?: KeyMapper<T> | undefined;
}>();

const emit = defineEmits<{
  (event: "redraw"): void;
  (event: "redrawSkip"): void;
}>();

defineSlots<{
  default?: (props: {
    item: T;
    column: number;
    columnCount: number;
    row: number;
    index: number;
  }) => VNode | VNode[] | Element | Element[];
}>();

const columns = ref<Column[]>([]);
const wall = useTemplateRef<HTMLDivElement>("wall");

// Track the previously seen items array to detect append-only changes
let previousItems: T[] = [];

function createColumns(count: number): Column[] {
  return Array.from({ length: count }).map(() => []);
}

function countIteratively(
  containerWidth: number,
  gapX: number,
  count: number,
  consumed: number,
): number {
  const nextWidth = getColumnWidthTarget(count);
  if (consumed + gapX + nextWidth <= containerWidth) {
    return countIteratively(
      containerWidth,
      gapX,
      count + 1,
      consumed + gapX + nextWidth,
    );
  }
  return count;
}

function getColumnWidthTarget(columnIndex: number): number {
  const widths = Array.isArray(columnWidth) ? columnWidth : [columnWidth];
  return widths[columnIndex % widths.length]!;
}

function columnCount(): number {
  const count = countIteratively(
    wall.value!.getBoundingClientRect().width,
    gapX,
    0,
    -gapX,
  );
  const boundedCount = aboveMin(belowMax(count));
  return boundedCount > 0 ? boundedCount : 1;
}

function belowMax(count: number): number {
  if (!maxColumns) {
    return count;
  }
  return Math.min(count, maxColumns);
}

function aboveMin(count: number): number {
  return Math.max(count, minColumns);
}

/**
 * Checks whether the new items array is a pure append of the previous one —
 * i.e. the existing items are unchanged and new items were only added at the end.
 * Returns the index at which new items start, or -1 if it's not a pure append.
 */
function getAppendStartIndex(newItems: T[]): number {
  if (newItems.length <= previousItems.length) {
    // Items were removed or count is the same — not a pure append
    return -1;
  }
  for (let i = 0; i < previousItems.length; i++) {
    if (newItems[i] !== previousItems[i]) {
      // An existing item changed position or value — not a pure append
      return -1;
    }
  }
  return previousItems.length;
}

if (ssrColumns > 0) {
  const newColumns = createColumns(ssrColumns);
  for (let i = 0; i < items.length; i++) {
    newColumns[i % ssrColumns]!.push(i);
  }
  columns.value = newColumns;
}

let currentRedrawId = 0;

async function fillColumns(itemIndex: number, assignedRedrawId: number) {
  if (itemIndex >= items.length) {
    return;
  }
  await nextTick();
  if (currentRedrawId !== assignedRedrawId) {
    return;
  }
  const columnDivs = [...wall.value!.children] as HTMLDivElement[];
  const target = columnDivs.reduce((prev, curr) =>
    curr.getBoundingClientRect().height < prev.getBoundingClientRect().height
      ? curr
      : prev,
  );
  columns.value[+target.dataset.index!]!.push(itemIndex);
  await fillColumns(itemIndex + 1, assignedRedrawId);
}

async function redraw(force = false) {
  const newColumnCount = columnCount();
  if (columns.value.length === newColumnCount && !force) {
    emit("redrawSkip");
    return;
  }
  columns.value = createColumns(newColumnCount);
  const scrollY = scrollContainer ? scrollContainer.scrollTop : window.scrollY;
  await fillColumns(0, ++currentRedrawId);
  if (scrollContainer) {
    scrollContainer.scrollBy({ top: scrollY - scrollContainer.scrollTop });
  } else {
    window.scrollTo({ top: scrollY });
  }
  emit("redraw");
}

/**
 * Appends only the new items to the existing layout, leaving already-placed
 * items untouched. Falls back to a full redraw if the column count has changed
 * or if the change wasn't a pure append.
 */
async function redrawAppend(startIndex: number) {
  const newColumnCount = columnCount();
  if (columns.value.length !== newColumnCount) {
    // Column count changed (e.g. container was resized at the same time),
    // so we can't trust the existing layout — fall back to full redraw.
    await redraw(true);
    return;
  }
  const placedCount = columns.value.reduce((sum, col) => sum + col.length, 0);
  const resumeIndex = Math.min(startIndex, placedCount);
  await fillColumns(resumeIndex, ++currentRedrawId);
  emit("redraw");
}

const resizeObserver =
  typeof ResizeObserver === "undefined"
    ? undefined
    : new ResizeObserver(debounce(() => redraw()));

onMounted(async () => {
  previousItems = [...items];
  await redraw();
  resizeObserver?.observe(wall.value!);
});

onBeforeUnmount(() => resizeObserver?.unobserve(wall.value!));

watch(
  () => items,
  (newItems) => {
    const appendStartIndex = getAppendStartIndex(newItems);
    if (appendStartIndex !== -1) {
      // Pure append — only fill in the new items
      previousItems = [...newItems];
      redrawAppend(appendStartIndex);
    } else {
      // Items were removed, reordered, or modified — full redraw needed
      previousItems = [...newItems];
      redraw(true);
    }
  },
);

watch(
  [
    () => columnWidth,
    () => gapX,
    () => gapY,
    () => minColumns,
    () => maxColumns,
  ],
  () => redraw(),
);
</script>

<template>
  <div
    ref="wall"
    class="masonry-wall"
    :style="{
      display: 'flex',
      gap: `${gapX}px`,
      flexDirection: rtl ? 'row-reverse' : undefined,
    }"
  >
    <div
      v-for="(column, columnIndex) in columns"
      :key="columnIndex"
      class="masonry-column"
      :data-index="columnIndex"
      :style="{
        display: 'flex',
        'flex-basis': `${getColumnWidthTarget(columnIndex)}px`,
        'flex-direction': 'column',
        'flex-grow': 1,
        gap: `${gapY}px`,
        height: 'max-content',
        'min-width': 0,
      }"
    >
      <div
        v-for="(itemIndex, row) in column"
        :key="
          keyMapper?.(items[itemIndex]!, columnIndex, row, itemIndex) ??
          itemIndex
        "
        class="masonry-item"
      >
        <slot
          :item="items[itemIndex]!"
          :column="columnIndex"
          :column-count="columns.length"
          :row="row"
          :index="itemIndex"
        >
          {{ items[itemIndex] }}
        </slot>
      </div>
    </div>
  </div>
</template>
